import type { Message, MessagePage } from '@/shared/api/schemas';

/** Сообщение в интерфейсе: с сервера или ещё отправляется (clientId, status). */
export type LocalMessage = Message & { readonly clientId?: string; readonly status?: 'sending' | 'queued' | 'failed' };

export interface LocalPage {
  readonly messages: readonly LocalMessage[];
  readonly hasMoreBefore: boolean;
  readonly hasMoreAfter: boolean;
}

export const PENDING_SEQ = Number.MAX_SAFE_INTEGER;

export function fromPage(page: MessagePage): LocalPage {
  return { messages: page.messages, hasMoreBefore: page.hasMoreBefore, hasMoreAfter: page.hasMoreAfter };
}

function sortMessages(list: LocalMessage[]): LocalMessage[] {
  return list.sort((a, b) => a.seq - b.seq || a.createdAt.localeCompare(b.createdAt));
}

/** Добавляет или заменяет сообщения: по id и по номеру черновика (clientId). */
export function upsertMessages(page: LocalPage, incoming: readonly LocalMessage[], clientId?: string | null): LocalPage {
  const list = page.messages.filter((m) => !incoming.some((i) => i.id === m.id) && (clientId === undefined || clientId === null || m.clientId !== clientId));
  // Номер черновика остаётся у отправленного сообщения: строка в ленте не пересоздаётся и не теряет фокус.
  const keep = (i: LocalMessage): LocalMessage => {
    const before = page.messages.find((m) => m.id === i.id || (clientId !== undefined && clientId !== null && m.clientId === clientId && i.status === undefined && incoming.length === 1));
    return before?.clientId !== undefined && i.clientId === undefined ? { ...i, clientId: before.clientId } : i;
  };
  return { ...page, messages: sortMessages([...list, ...incoming.map(keep)]) };
}

export function replaceMessage(page: LocalPage, message: Message): LocalPage {
  if (!page.messages.some((m) => m.id === message.id)) return page;
  return { ...page, messages: page.messages.map((m) => (m.id === message.id ? (m.clientId === undefined ? message : { ...message, clientId: m.clientId }) : m)) };
}

export function removeMessage(page: LocalPage, id: string): LocalPage {
  return { ...page, messages: page.messages.filter((m) => m.id !== id && m.clientId !== id) };
}

export function patchMessage(page: LocalPage, id: string, patch: (m: LocalMessage) => LocalMessage): LocalPage {
  if (!page.messages.some((m) => m.id === id)) return page;
  return { ...page, messages: page.messages.map((m) => (m.id === id ? patch(m) : m)) };
}

/** Удалённое сообщение остаётся в ленте, только если у него есть ответы. */
export function visibleInChannel(m: Message): boolean {
  return !m.deleted || m.replyCount > 0;
}
